import React, { createContext, useContext, useState, useRef, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { AudioTrack as Track } from '../types/music';
import { logError } from '../utils/logger';

interface AudioContextType {
  currentTrack: Track | null;
  isPlaying: boolean;
  progress: number;
  duration: number;
  volume: number;
  isPreviewMode: boolean;
  previewTimeRemaining: number;
  play: (track: Track) => void;
  pause: () => void;
  playTrack: (track: Track) => void;
  togglePlayPause: () => void;
  setVolume: (value: number) => void;
  seek: (time: number) => void;
  nextTrack: () => void;
  prevTrack: () => void;
  addToQueue: (track: Track) => void;
  removeFromQueue: (id: string) => void;
}

const defaultContext: AudioContextType = {
  currentTrack: null,
  isPlaying: false,
  progress: 0,
  duration: 0,
  volume: 0.8,
  isPreviewMode: true,
  previewTimeRemaining: 30,
  play: () => {},
  pause: () => {},
  playTrack: () => {},
  togglePlayPause: () => {},
  setVolume: () => {},
  seek: () => {},
  nextTrack: () => {},
  prevTrack: () => {},
  addToQueue: () => {},
  removeFromQueue: () => {},
};

const AudioContext = createContext<AudioContextType>(defaultContext);

// Provider component
export const AudioProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.8);
  const [_queue, setQueue] = useState<Track[]>([]);
  const [_history, setHistory] = useState<Track[]>([]);
  
  // Preview mode state - DISABLED for full playback via DISCO
  const [isPreviewMode] = useState(false); // Full playback enabled
  const [previewTimeRemaining, setPreviewTimeRemaining] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fadeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Create audio element if it doesn't exist
    if (!audioRef.current) {
      audioRef.current = new Audio();
      
      // Add event listeners
      audioRef.current.addEventListener('timeupdate', updateProgress);
      audioRef.current.addEventListener('loadedmetadata', setAudioDuration);
      audioRef.current.addEventListener('ended', handleTrackEnd);
      
      // Set initial volume
      audioRef.current.volume = volume;
    }
    
    // Cleanup function
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', updateProgress);
        audioRef.current.removeEventListener('loadedmetadata', setAudioDuration);
        audioRef.current.removeEventListener('ended', handleTrackEnd);
        audioRef.current.pause();
      }
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
      }
    };
  }, []);
  
  // Set audio source when currentTrack changes
  useEffect(() => {
    if (currentTrack && audioRef.current) {
      audioRef.current.src = currentTrack.url;
      audioRef.current.load();
      
      // Reset preview state (kept for compatibility but not used)
      setPreviewTimeRemaining(0);
      
      // Reset volume to default and clear any fade
      if (audioRef.current) {
        audioRef.current.volume = volume;
      }
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
      
      // Set up audio event listeners
      const audio = audioRef.current;
      audio.addEventListener('loadedmetadata', setAudioDuration);
      audio.addEventListener('ended', handleTrackEnd);

      if (isPlaying) {
        audioRef.current.play()
          .catch(error => {
            logError(error, { component: 'AudioContext', action: 'playOnTrackChange' });
            setIsPlaying(false);
          });
      }

      // Cleanup event listeners
      return () => {
        audio.removeEventListener('loadedmetadata', setAudioDuration);
        audio.removeEventListener('ended', handleTrackEnd);
      };
    }
  }, [currentTrack]);

  // Handle play/pause changes
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play()
          .catch(error => {
            logError(error, { component: 'AudioContext', action: 'playPauseToggle' });
            setIsPlaying(false);
          });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);
  
  // Set up progress update interval
  useEffect(() => {
    let progressInterval: NodeJS.Timeout | null = null;
    
    if (isPlaying && audioRef.current) {
      // Update progress every 100ms for smooth progress bar
      progressInterval = setInterval(updateProgress, 100);
    }
    
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [isPlaying]);
  
  // Update progress
  const updateProgress = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;
      setProgress(currentTime);
    }
  };

  // Set audio duration
  const setAudioDuration = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  // Handle track end
  const handleTrackEnd = () => {
    nextTrack();
  };
  
  // Play a track - memoized to prevent unnecessary rerenders
  const play = useCallback((track: Track) => {
    setCurrentTrack(prevTrack => {
      // Add current track to history if it exists
      if (prevTrack) {
        setHistory(prev => [prevTrack, ...prev.slice(0, 19)]);
      }
      return track;
    });
    setIsPlaying(true);
  }, []);

  // Pause playback - memoized
  const pause = useCallback(() => {
    setIsPlaying(false);
    // Clear any active fade when pausing
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }
  }, []);

  // Toggle play/pause - use functional setState for stable callback
  const togglePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  // Set volume - memoized
  const setVolume = useCallback((value: number) => {
    if (audioRef.current) {
      audioRef.current.volume = value;
      setVolumeState(value);
      // Clear any active fade when manually setting volume
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
    }
  }, []);

  // Seek to position - memoized
  const seek = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setProgress(time);
    }
  }, []);

  // Play next track - memoized
  const nextTrack = useCallback(() => {
    setQueue(currentQueue => {
      if (currentQueue.length > 0) {
        const next = currentQueue[0];
        const newQueue = currentQueue.slice(1);

        setCurrentTrack(prevTrack => {
          if (prevTrack) {
            setHistory(prev => [prevTrack, ...prev.slice(0, 19)]);
          }
          return next;
        });
        setIsPlaying(true);
        return newQueue;
      } else {
        setIsPlaying(false);
        return currentQueue;
      }
    });
  }, []);

  // Play previous track - memoized
  const prevTrack = useCallback(() => {
    setHistory(currentHistory => {
      if (currentHistory.length > 0) {
        const prev = currentHistory[0];
        const newHistory = currentHistory.slice(1);

        setCurrentTrack(currTrack => {
          if (currTrack) {
            setQueue(q => [currTrack, ...q]);
          }
          return prev;
        });
        setIsPlaying(true);
        return newHistory;
      }
      return currentHistory;
    });
  }, []);

  // Add track to queue - memoized
  const addToQueue = useCallback((track: Track) => {
    setQueue(prev => [...prev, track]);
  }, []);

  // Remove track from queue - memoized
  const removeFromQueue = useCallback((id: string) => {
    setQueue(prev => prev.filter(track => track.id !== id));
  }, []);

  // Memoize context value to prevent unnecessary rerenders
  // Note: progress updates frequently (10x/sec) - components that only need controls
  // should destructure only what they need to minimize rerenders
  const value = useMemo(() => ({
    currentTrack,
    isPlaying,
    progress,
    duration,
    volume,
    isPreviewMode,
    previewTimeRemaining,
    play,
    pause,
    playTrack: play,
    togglePlayPause,
    setVolume,
    seek,
    nextTrack,
    prevTrack,
    addToQueue,
    removeFromQueue,
  }), [
    currentTrack,
    isPlaying,
    progress,
    duration,
    volume,
    isPreviewMode,
    previewTimeRemaining,
    play,
    pause,
    togglePlayPause,
    setVolume,
    seek,
    nextTrack,
    prevTrack,
    addToQueue,
    removeFromQueue,
  ]);

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};

// Custom hook to use the audio context
export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

export default AudioContext;